import { useState } from "preact/compat"
import { useAsyncEffect } from "@/utils"
import {
  IntegrationWithJobInfo,
  getIntegrationsWithJobs,
  findIntegrationByName,
} from "@/api/integration"
import { LoadingSpinner } from "@/app/loading"
import { Button } from "@/components/ui/button"
import { useLocation } from "wouter-preact"
import { SettingsLayout } from "@/settings/layout"
import { IntegrationSettingsLayout, SettingsSection } from "../layout"
import keheLogo from "/img/integration/kehe.png"
import freshdeskLogo from "/img/integration/freshdesk.png"
import { KeheConfigForm } from "@/setup/config/kehe/page"
import { SyncStatusRow, IntegrationLogo } from "../sync-status-row"
import { ContractsList } from "@/contracts/contracts-list"
import { KeheUdrSettings } from "./kehe-udr-settings"
import { UpdateCredentialsForm } from "../update-credentials-form"

export function KeheSettingsPage() {
  const [, setLocation] = useLocation()
  const [keheIntegration, setKeheIntegration] = useState<IntegrationWithJobInfo | null>(null)
  const [freshdeskIntegration, setFreshdeskIntegration] = useState<IntegrationWithJobInfo | null>(null)
  const [emailAccounts, setEmailAccounts] = useState<IntegrationWithJobInfo[]>([])
  const [unfiIntegration, setUnfiIntegration] = useState<IntegrationWithJobInfo | null>(null)
  const [loading, setLoading] = useState(true)

  async function refreshIntegrations() {
    const res = await getIntegrationsWithJobs()
    if (res.ok) {
      const integrations = res.value.data
      setEmailAccounts(integrations.filter((i: IntegrationWithJobInfo) => ["gmail", "microsoft"].includes(i.name)))
      const kehe = findIntegrationByName(integrations, "kehe")
      const freshdesk = findIntegrationByName(integrations, "freshdesk")
      const unfi = findIntegrationByName(integrations, "unfi")
      if (unfi) {
        setUnfiIntegration(unfi)
      }
      if (kehe) {
        setKeheIntegration(kehe)
      }
      if (freshdesk) {
        if (freshdesk.username !== kehe?.username) {
          freshdesk.last_sync_job = {
            id: "00000000-0000-0000-0000-000000000000",
            created_at: freshdesk.created_at,
            error: "The freshdesk username does not match the Kehe username",
          }
        }
        setFreshdeskIntegration(freshdesk)
      }
    }
  }

  useAsyncEffect(async () => {
    await refreshIntegrations()
    setLoading(false)
  }, [])

  if (loading) {
    return (
      <SettingsLayout>
        <div class="flex justify-center py-12">
          <LoadingSpinner />
        </div>
      </SettingsLayout>
    )
  }

  if (!keheIntegration) {
    return (
      <SettingsLayout>
        <div class="text-center py-12">
          <p class="text-gray-500 mb-4">KeHE integration not found</p>
          <Button variant="outline" onClick={() => setLocation("/settings/connections")}>
            Back to Connections
          </Button>
        </div>
      </SettingsLayout>
    )
  }

  return (
    <IntegrationSettingsLayout
      logo={keheLogo}
      name="KeHE"
      username={keheIntegration.username}
      integrationId={keheIntegration.id}>
      <SettingsSection title="Sync Status">
        <div class="divide-y divide-gray-100">
          <SyncStatusRow
            logo={keheLogo}
            name="KeHE"
            username={keheIntegration.username}
            lastSyncJob={keheIntegration.last_sync_job}
            integrationName="kehe">
            <UpdateCredentialsForm
              integrationName="kehe"
              integrationId={keheIntegration.id}
              initialUsername={keheIntegration.username}
              credentialUpdatedAt={keheIntegration.credential_updated_at}
              onSuccess={refreshIntegrations}
            />
          </SyncStatusRow>
          {freshdeskIntegration ? (
            <SyncStatusRow
              logo={freshdeskLogo}
              name="Freshdesk"
              username={freshdeskIntegration.username}
              lastSyncJob={freshdeskIntegration.last_sync_job}
              integrationName="freshdesk">
              <UpdateCredentialsForm
                integrationName="freshdesk"
                integrationId={freshdeskIntegration.id}
                initialUsername={freshdeskIntegration.username}
                credentialUpdatedAt={freshdeskIntegration.credential_updated_at}
                onSuccess={refreshIntegrations}
              />
            </SyncStatusRow>
          ) : (
            <div class="flex items-center gap-4 py-1">
              <IntegrationLogo src={freshdeskLogo} alt="Freshdesk" />
              <div class="text-sm text-gray-400">Freshdesk not connected</div>
              <Button
                variant="outline"
                size="sm"
                class="ml-auto"
                onClick={() => setLocation("/settings/connect/kehe-freshdesk")}>
                Connect Freshdesk
              </Button>
            </div>
          )}
        </div>
      </SettingsSection>

      <SettingsSection title="UDR Notifications">
        <KeheUdrSettings
          integration={keheIntegration}
          emailAccounts={emailAccounts}
          unfiIntegration={unfiIntegration}
        />
      </SettingsSection>

      <SettingsSection title="Configuration">
        <KeheConfigForm integration={keheIntegration} onSuccess={() => location.reload()} />
      </SettingsSection>

      <SettingsSection title="Contract Documents">
        <ContractsList source="kehe" integrationId={keheIntegration.id} />
      </SettingsSection>
    </IntegrationSettingsLayout>
  )
}
